import { useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Flex,
  Button,
  Text,
  Box,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormikInput from '../../components/formikinput';
import FormikSelect from '../../components/forkmikselect';
import useEditAdminMutation from '../../api/mutations/useEditAdminMutation';

const EditAdminModal = ({ isOpen, onClose, admin }) => {
  const toast = useToast();

  const editAdminMutation = useEditAdminMutation({
    onSuccess: () => {
      toast({
        title: 'Employee updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: error?.response?.data?.message || 'Failed to update employee',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const formik = useFormik({
    initialValues: {
      first_name: '',
      surname: '',
      role: 'employee',
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required('First name is required'),
      surname: Yup.string().required('Last name is required'),
      role: Yup.string().required('Role is required'),
    }),
    onSubmit: values => {
      editAdminMutation.mutate({
        _id: admin?._id || admin?.id,
        first_name: values.first_name,
        surname: values.surname,
        role: values.role,
      });
    },
  });

  useEffect(() => {
    if (admin && isOpen) {
      formik.setValues({
        first_name: admin?.first_name || '',
        surname: admin?.surname || '',
        role: admin?.authUser?.role || 'employee',
      });
    }
  }, [admin, isOpen]);

  if (!admin) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        minH={'200px'}
        maxW={'500px'}
        borderRadius={16}
        padding={4}
        gap={8}
      >
        <ModalBody display={'flex'} flexDirection={'column'} gap={6}>
          <Text
            fontFamily={'PlusJakartaSansSemiBold'}
            fontSize={'24px'}
            lineHeight={'32px'}
            textTransform={'capitalize'}
            color={'#000000'}
          >
            Edit Employee
          </Text>
          <Box height="1px" width="100%" bg="#8A8F9333" mx="auto" />
          <Flex direction={'column'} gap={4}>
            <Flex gap={4}>
              <FormikInput
                formik={formik}
                name={'first_name'}
                label={'First Name'}
                placeholder={'Enter first name'}
              />
              <FormikInput
                formik={formik}
                name={'surname'}
                label={'Last Name'}
                placeholder={'Enter last name'}
              />
            </Flex>
            <Box>
              <Text
                fontFamily={'PlusJakartaSansMedium'}
                fontSize={'14px'}
                color={'#898989'}
                mb={2}
              >
                Email
              </Text>
              <input
                type="email"
                value={admin?.email_address || ''}
                disabled
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#F7FAFC',
                  color: '#718096',
                  fontSize: '14px',
                  fontFamily: 'PlusJakartaSansMedium',
                  cursor: 'not-allowed',
                }}
              />
            </Box>
            <FormikSelect
              formik={formik}
              name="role"
              label="Role"
            >
              <option value="employee">Employee</option>
              <option value="owner">Owner</option>
            </FormikSelect>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex w={'100%'} justify={'flex-end'} gap={4}>
            <Button
              bg={'white'}
              border={'1px solid #000000'}
              color="#000000"
              borderRadius={8}
              paddingInline={16}
              textTransform={'uppercase'}
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              onClick={formik.handleSubmit}
              isDisabled={editAdminMutation.isLoading}
              bg={'#000000'}
              color="#FFFFFF"
              borderRadius={8}
              paddingInline={16}
              textTransform={'uppercase'}
              border={'1px solid #FFFFFF'}
            >
              {editAdminMutation.isLoading ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditAdminModal;
